import React, { useState, useEffect, useRef } from 'react';
import AeronyxArchitecture from './AeronyxArchitecture';

// ============================================
// CUSTOM HOOKS
// ============================================
const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated) {
        setIsInView(true);
        setHasAnimated(true);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasAnimated, options]);

  return [ref, isInView];
};

// ============================================
// ANIMATED COUNTER COMPONENT
// ============================================
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView();

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// ============================================
// SVG ICON COMPONENTS
// ============================================
const Icons = {
  plane: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2L11 13M22 2L15 22L11 13L2 9L22 2Z"/>
    </svg>
  ),
  booking: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  disruption: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  loyalty: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  operations: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
      <path d="M2 17l10 5 10-5"/>
      <path d="M2 12l10 5 10-5"/>
    </svg>
  ),
  revenue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  support: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  brain: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
    </svg>
  ),
  plug: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/>
      <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"/>
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  rocket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  arrowRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  arrowLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  chevronDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  ),
  quote: (
    <svg viewBox="0 0 24 24" fill="currentColor" opacity="0.15">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
    </svg>
  ),
  message: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  smartphone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
      <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      <line x1="9" y1="10" x2="15" y2="10"/>
      <line x1="12" y1="7" x2="12" y2="13"/>
    </svg>
  )
};

const Icon = ({ name, size = 24, color = 'currentColor', style = {} }) => (
  <span style={{ display: 'inline-flex', width: size, height: size, color, flexShrink: 0, ...style }}>
    {Icons[name]}
  </span>
);

// ============================================
// AERONYX LOGO COMPONENT
// ============================================
const AeronyxLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="aeronyxMain" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06b6d4" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
      <linearGradient id="aeronyxAccent" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8b5cf6" />
        <stop offset="100%" stopColor="#ec4899" />
      </linearGradient>
    </defs>
    {/* Main wing shape - sleek, modern */}
    <path
      d="M6 26L42 10L38 18L22 24L14 38L6 26Z"
      fill="url(#aeronyxMain)"
    />
    {/* Secondary wing element */}
    <path
      d="M14 38L22 24L26 32L18 42L14 38Z"
      fill="url(#aeronyxAccent)"
      opacity="0.85"
    />
    {/* Speed accent line */}
    <path
      d="M4 28L12 24"
      stroke="#06b6d4"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.6"
    />
    <path
      d="M6 34L10 32"
      stroke="#8b5cf6"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.4"
    />
  </svg>
);

// ============================================
// SHARED DATA
// ============================================
const agentsData = [
  { id: 'booking', name: 'Booking Agent', icon: 'booking', color: '#06b6d4',
    description: 'Handles complex itineraries, multi-city trips, seat selection, and ancillary bundling through conversational AI.',
    features: ['Voice & text booking', 'Smart seat selection', 'Fare comparison', 'Ancillary upsell'],
    capabilities: ['Natural language flight search', 'Multi-city itinerary construction', 'Real-time seat map selection', 'Ancillary bundling', 'PNR creation', 'Fare comparison'],
    mcpTools: [
      { name: 'search_availability', params: 'origin, dest, dates, pax', returns: 'FlightOption[]' },
      { name: 'create_pnr', params: 'itinerary, passengers', returns: 'PNR' },
      { name: 'get_seat_map', params: 'flight, cabin', returns: 'SeatMap' },
      { name: 'add_ancillary', params: 'pnr, type', returns: 'Confirmation' }
    ],
    integrations: ['PSS (Amadeus/Sabre)', 'Payment Gateway', 'Seat Inventory'],
    stateManagement: ['Search context', 'Selected flights', 'Passenger manifest']
  },
  { id: 'disruption', name: 'Disruption Agent', icon: 'disruption', color: '#f59e0b',
    description: 'Detects delays and cancellations before passengers know, automatically rebooking and calculating compensation.',
    features: ['Real-time monitoring', 'Auto rebooking', 'EU261 compliance', 'Voucher issuance'],
    capabilities: ['Real-time delay detection', 'Automated rebooking', 'EU261 compensation', 'Alternative routing', 'Voucher issuance'],
    mcpTools: [
      { name: 'get_flight_status', params: 'flight, date', returns: 'FlightStatus' },
      { name: 'find_alternatives', params: 'segment', returns: 'Alternative[]' },
      { name: 'rebook_passenger', params: 'pnr, itinerary', returns: 'Confirmation' },
      { name: 'issue_voucher', params: 'pnr, type', returns: 'Voucher' }
    ],
    integrations: ['OCC', 'Hotel Inventory', 'Ground Transport'],
    stateManagement: ['Disruption context', 'Rebooking attempts', 'Compensation status']
  },
  { id: 'loyalty', name: 'Loyalty Agent', icon: 'loyalty', color: '#8b5cf6',
    description: 'Manages frequent flyer interactions from balance queries to award redemptions and upgrade requests.',
    features: ['Points management', 'Award booking', 'Status benefits', 'Partner earning'],
    capabilities: ['Points balance tracking', 'Award search & redemption', 'Tier benefits', 'Upgrade management'],
    mcpTools: [
      { name: 'get_balance', params: 'member_id', returns: 'PointsBalance' },
      { name: 'search_awards', params: 'origin, dest', returns: 'AwardOption[]' },
      { name: 'redeem_award', params: 'member_id, award', returns: 'Redemption' }
    ],
    integrations: ['FFP Platform', 'Partner Networks'],
    stateManagement: ['Member context', 'Transaction history']
  },
  { id: 'operations', name: 'Operations Agent', icon: 'operations', color: '#10b981',
    description: 'Provides real-time operational visibility to passengers and staff with gate, delay, and connection information.',
    features: ['Live tracking', 'Gate updates', 'Connection alerts', 'Weather impact'],
    capabilities: ['Real-time flight tracking', 'Gate information', 'Crew status', 'Weather assessment'],
    mcpTools: [
      { name: 'get_flight_info', params: 'flight, date', returns: 'FlightDetails' },
      { name: 'get_aircraft_position', params: 'reg', returns: 'Position' },
      { name: 'check_connections', params: 'flight', returns: 'Connection[]' }
    ],
    integrations: ['OCC', 'Crew Management', 'Weather Services'],
    stateManagement: ['Flight watch list', 'Alert subscriptions']
  },
  { id: 'revenue', name: 'Revenue Agent', icon: 'revenue', color: '#ec4899',
    description: 'Maximizes revenue through personalized offers, fare optimization, and strategic ancillary recommendations.',
    features: ['Dynamic offers', 'Fare optimization', 'Promo validation', 'Refund handling'],
    capabilities: ['Dynamic fare explanation', 'Upsell recommendations', 'Refund calculations', 'Promo validation'],
    mcpTools: [
      { name: 'get_fare_rules', params: 'fare_basis', returns: 'FareRules' },
      { name: 'calculate_refund', params: 'pnr', returns: 'RefundAmount' },
      { name: 'get_upsell_offers', params: 'pnr', returns: 'UpsellOffer[]' }
    ],
    integrations: ['RMS', 'Fare Filing', 'Promo Engine'],
    stateManagement: ['Pricing context', 'Applied discounts']
  },
  { id: 'support', name: 'Support Agent', icon: 'support', color: '#3b82f6',
    description: 'Resolves issues instantly with smart FAQ handling, baggage tracking, and seamless human escalation.',
    features: ['Instant answers', 'Baggage tracking', 'Case management', 'Smart escalation'],
    capabilities: ['FAQ handling', 'Complaint intake', 'Baggage tracking', 'Human escalation'],
    mcpTools: [
      { name: 'search_knowledge', params: 'query', returns: 'Article[]' },
      { name: 'create_case', params: 'pnr, desc', returns: 'CaseNumber' },
      { name: 'track_baggage', params: 'tag', returns: 'BaggageStatus' }
    ],
    integrations: ['CRM', 'Knowledge Base', 'Baggage System'],
    stateManagement: ['Case context', 'Interaction history']
  }
];

// ============================================
// CSS STYLES
// ============================================
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Space+Mono:wght@400;700&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  button, input, select, textarea { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-track {
    background: #0f172a;
    border-radius: 5px;
  }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #06b6d4, #8b5cf6);
    border-radius: 5px;
    border: 2px solid #0f172a;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #22d3ee, #a78bfa);
  }
  ::-webkit-scrollbar-corner {
    background: #0f172a;
  }
  /* Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: #8b5cf6 #0f172a;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
  }

  @keyframes pulse-glow {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
  }

  @keyframes slide-up {
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes borderGlow {
    0%, 100% { box-shadow: 0 0 20px rgba(6, 182, 212, 0.3); }
    50% { box-shadow: 0 0 40px rgba(6, 182, 212, 0.6); }
  }

  @keyframes typing {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @keyframes soundwave {
    0% { height: 8px; }
    100% { height: 20px; }
  }

  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes particle {
    0% { transform: translateY(0) rotate(0deg); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(-100vh) rotate(720deg); opacity: 0; }
  }

  @keyframes progressBar {
    from { width: 0%; }
    to { width: 100%; }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .nav-link { color: #94a3b8; text-decoration: none; font-size: 14px; font-weight: 500; transition: color 0.3s; }
  .nav-link:hover { color: white; }

  .cta-button {
    background: linear-gradient(135deg, #06b6d4, #8b5cf6);
    color: white;
    border: none;
    padding: 14px 32px;
    border-radius: 50px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 30px rgba(6, 182, 212, 0.3);
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .cta-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 40px rgba(6, 182, 212, 0.5);
  }

  .secondary-button {
    background: transparent;
    color: white;
    border: 1px solid rgba(255,255,255,0.2);
    padding: 14px 32px;
    border-radius: 50px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
  }
  .secondary-button:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.4);
  }

  .gradient-text {
    background: linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .grid-bg {
    background-image: linear-gradient(rgba(6, 182, 212, 0.03) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(6, 182, 212, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
  }

  .glass-card {
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .hover-lift {
    transition: all 0.3s ease;
  }
  .hover-lift:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  .fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .stagger-1 { animation-delay: 0.1s; }
  .stagger-2 { animation-delay: 0.2s; }
  .stagger-3 { animation-delay: 0.3s; }
  .stagger-4 { animation-delay: 0.4s; }

  /* Mobile Navigation */
  .mobile-menu-btn {
    display: none;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
  }

  .mobile-nav {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(3, 7, 18, 0.98);
    backdrop-filter: blur(20px);
    z-index: 1100;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 32px;
  }

  .mobile-nav.open {
    display: flex;
  }

  .mobile-nav-link {
    color: white;
    text-decoration: none;
    font-size: 24px;
    font-weight: 600;
    transition: color 0.3s;
  }

  .mobile-nav-link:hover {
    color: #06b6d4;
  }

  .mobile-close-btn {
    position: absolute;
    top: 24px;
    right: 24px;
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    z-index: 1001;
  }

  /* Responsive Styles */
  @media (max-width: 1024px) {
    .desktop-nav { display: none !important; }
    .mobile-menu-btn { display: block; }
  }

  @media (max-width: 968px) {
    .agents-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }

  @media (max-width: 768px) {
    .section-padding { padding: 80px 20px !important; }
    .section-title { font-size: 36px !important; }
    .section-subtitle { font-size: 16px !important; }
    .orbiting-agents { display: none !important; }
    .connection-lines { display: none !important; }
    .hero-demo-section { display: none !important; }
    .agents-grid { grid-template-columns: 1fr !important; }
  }

  @media (max-width: 480px) {
    .section-padding { padding: 60px 16px !important; }
    .section-title { font-size: 28px !important; }
    .omnichannel-demo { padding: 16px !important; border-radius: 16px !important; }
  }
`;

// ============================================
// STAT CARD COMPONENT
// ============================================
const StatCard = ({ stat, index }) => {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className="glass-card hover-lift"
      style={{
        borderRadius: '20px',
        padding: '32px',
        textAlign: 'center',
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.6s ease-out ${index * 0.1}s`
      }}
    >
      <div style={{ width: '56px', height: '56px', background: `${stat.color}20`, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <Icon name={stat.icon} size={28} color={stat.color} />
      </div>
      <div style={{ fontSize: '42px', fontWeight: '700', fontFamily: '"Space Mono", monospace', marginBottom: '8px' }} className="gradient-text">
        {stat.prefix}
        <AnimatedCounter end={stat.value > 1000 ? stat.value / 1000000 : stat.value} suffix={stat.value > 1000 ? 'M' : ''} duration={2000} />
        {stat.suffix}
      </div>
      <div style={{ fontSize: '15px', color: '#94a3b8', fontWeight: '500' }}>{stat.label}</div>
    </div>
  );
};

// ============================================
// HOW IT WORKS - TRENDY BENTO GRID DESIGN
// ============================================
const stepColors = ['#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b'];

const HowItWorksSection = ({ howItWorks }) => {
  const [ref, isInView] = useInView();
  const [activeStep, setActiveStep] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // Auto-rotate through steps
  useEffect(() => {
    if (isHovering) return;
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(timer);
  }, [isHovering]);

  return (
    <section id="how-it-works" style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)', background: 'linear-gradient(180deg, transparent, rgba(139, 92, 246, 0.02))' }}>
      <div ref={ref} style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Section Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 8vw, 64px)' }}>
          <div style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '20px',
            fontSize: '14px',
            color: '#a78bfa',
            marginBottom: '20px'
          }}>
            Simple Integration
          </div>
          <h2 style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '700', marginBottom: '20px', letterSpacing: '-1px' }}>
            Get Started in <span className="gradient-text">Four Steps</span>
          </h2>
          <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#94a3b8', maxWidth: '600px', margin: '0 auto', lineHeight: '1.6' }}>
            From integration to optimization, our streamlined process gets your AI agents operational in weeks, not months.
          </p>
        </div>

        {/* Bento Grid Container */}
        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Left side - Step selector */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {howItWorks.map((step, i) => {
            const color = stepColors[i];
            const isActive = activeStep === i;
            return (
              <div
                key={i}
                onClick={() => setActiveStep(i)}
                onMouseEnter={() => { setActiveStep(i); setIsHovering(true); }}
                onMouseLeave={() => setIsHovering(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '20px 24px',
                  background: isActive
                    ? `linear-gradient(135deg, ${color}15, ${color}05)`
                    : 'rgba(15, 23, 42, 0.4)',
                  border: `1px solid ${isActive ? color + '40' : 'rgba(255, 255, 255, 0.06)'}`,
                  borderRadius: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Progress bar for active */}
                {isActive && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '2px',
                    background: `linear-gradient(90deg, ${color}, transparent)`,
                    animation: 'progressBar 3s linear',
                    width: '100%'
                  }} />
                )}

                {/* Step number */}
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: isActive ? color : 'rgba(255, 255, 255, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: isActive ? 'white' : '#64748b',
                  transition: 'all 0.4s',
                  flexShrink: 0
                }}>
                  {i + 1}
                </div>

                {/* Step info */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: isActive ? 'white' : '#94a3b8',
                    marginBottom: '4px',
                    transition: 'color 0.3s'
                  }}>
                    {step.title}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#64748b',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {step.description.split('.')[0]}
                  </div>
                </div>

                {/* Arrow indicator */}
                <div style={{
                  transform: isActive ? 'translateX(0)' : 'translateX(-8px)',
                  opacity: isActive ? 1 : 0,
                  transition: 'all 0.3s',
                  color: color
                }}>
                  <Icon name="arrowRight" size={20} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Right side - Feature showcase */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.4))',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          padding: 'clamp(24px, 5vw, 48px)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 'clamp(400px, 60vw, 480px)'
        }}>
          {/* Animated background gradient */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '100%',
            height: '100%',
            background: `radial-gradient(circle, ${stepColors[activeStep]}20 0%, transparent 60%)`,
            transition: 'all 0.6s ease-out',
            pointerEvents: 'none'
          }} />

          {/* Grid pattern overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
            `,
            backgroundSize: '32px 32px',
            pointerEvents: 'none'
          }} />

          {/* Content */}
          {howItWorks.map((step, i) => {
            const color = stepColors[i];
            const isActive = activeStep === i;
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: 'clamp(20px, 4vw, 48px)',
                  left: 'clamp(16px, 4vw, 48px)',
                  right: 'clamp(16px, 4vw, 48px)',
                  bottom: 'clamp(60px, 8vw, 80px)',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateX(0) scale(1)' : 'translateX(20px) scale(0.98)',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  pointerEvents: isActive ? 'auto' : 'none'
                }}
              >
                {/* Top badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: `${color}15`,
                  border: `1px solid ${color}30`,
                  borderRadius: '50px',
                  padding: '6px 16px',
                  width: 'fit-content',
                  marginBottom: '24px'
                }}>
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: color,
                    animation: 'pulse-glow 2s infinite'
                  }} />
                  <span style={{ fontSize: '12px', color: color, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Step {i + 1}
                  </span>
                </div>

                {/* Large icon */}
                <div style={{
                  width: 'clamp(60px, 12vw, 100px)',
                  height: 'clamp(60px, 12vw, 100px)',
                  background: `linear-gradient(135deg, ${color}20, transparent)`,
                  border: `2px solid ${color}30`,
                  borderRadius: 'clamp(16px, 3vw, 28px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 'clamp(16px, 4vw, 32px)',
                  position: 'relative'
                }}>
                  {/* Animated ring */}
                  <div style={{
                    position: 'absolute',
                    inset: '-8px',
                    border: `2px dashed ${color}30`,
                    borderRadius: 'clamp(24px, 4vw, 36px)',
                    animation: 'spin 20s linear infinite'
                  }} />
                  <Icon name={step.icon} size={48} color={color} style={{ width: 'clamp(28px, 6vw, 48px)', height: 'clamp(28px, 6vw, 48px)' }} />
                </div>

                {/* Title */}
                <h3 style={{
                  fontSize: 'clamp(22px, 4vw, 32px)',
                  fontWeight: '700',
                  marginBottom: '16px',
                  letterSpacing: '-0.5px'
                }}>
                  {step.title}
                </h3>

                {/* Description */}
                <p style={{
                  fontSize: 'clamp(14px, 2vw, 16px)',
                  color: '#94a3b8',
                  lineHeight: '1.8',
                  maxWidth: '400px',
                  marginBottom: 'clamp(16px, 4vw, 32px)'
                }}>
                  {step.description}
                </p>

                {/* Feature pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 'auto' }}>
                  {['Fast Setup', 'No Downtime', 'Full Support'].map((feature, j) => (
                    <span key={j} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#cbd5e1'
                    }}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Step indicators at bottom */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            right: '48px',
            display: 'flex',
            gap: '8px',
            zIndex: 10
          }}>
            {[0, 1, 2, 3].map(i => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                style={{
                  width: activeStep === i ? '32px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: activeStep === i
                    ? `linear-gradient(90deg, ${stepColors[i]}, ${stepColors[(i + 1) % 4]})`
                    : 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.4s'
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom metrics bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginTop: '32px',
        maxWidth: '1200px',
        margin: '32px auto 0'
      }}>
        {[
          { label: 'Average Setup Time', value: '6 weeks', icon: 'clock' },
          { label: 'System Uptime', value: '99.99%', icon: 'chart' },
          { label: 'Languages Supported', value: '40+', icon: 'globe' },
          { label: 'Customer Success Rate', value: '100%', icon: 'check' }
        ].map((metric, i) => (
          <div key={i} style={{
            background: 'rgba(15, 23, 42, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: '12px',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: `${stepColors[i]}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon name={metric.icon} size={20} color={stepColors[i]} />
            </div>
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '2px' }}>{metric.value}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{metric.label}</div>
            </div>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
};

// ============================================
// AGENT CARD COMPONENT
// ============================================
const AgentCard = ({ agent, isHovered, onHover, onLeave }) => {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{
        background: isHovered ? `linear-gradient(135deg, ${agent.color}15, ${agent.color}05)` : 'rgba(15, 23, 42, 0.6)',
        border: `1px solid ${isHovered ? agent.color + '50' : 'rgba(255, 255, 255, 0.08)'}`,
        borderRadius: '24px',
        padding: '32px',
        transition: 'all 0.4s ease',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        boxShadow: isHovered ? `0 20px 40px ${agent.color}20` : 'none',
        opacity: isInView ? 1 : 0,
        cursor: 'pointer'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{
          width: '60px',
          height: '60px',
          background: `linear-gradient(135deg, ${agent.color}30, ${agent.color}10)`,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s',
          transform: isHovered ? 'scale(1.1)' : 'scale(1)'
        }}>
          <Icon name={agent.icon} size={30} color={agent.color} />
        </div>
      </div>
      <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: agent.color }}>{agent.name}</h3>
      <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '20px', lineHeight: '1.6' }}>{agent.description}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
        {agent.features.map((f, j) => (
          <span key={j} style={{
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            color: '#cbd5e1'
          }}>
            {f}
          </span>
        ))}
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: '16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <span style={{ fontSize: '12px', color: '#64748b' }}>{agent.mcpTools.length} MCP tools</span>
        <span style={{ fontSize: '12px', color: agent.color, display: 'flex', alignItems: 'center', gap: '4px' }}>
          Learn more <Icon name="arrowRight" size={14} />
        </span>
      </div>
    </div>
  );
};

// ============================================
// PRICING CARD COMPONENT
// ============================================
const PricingCard = ({ plan, index }) => {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      style={{
        background: plan.highlighted
          ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.1))'
          : 'rgba(15, 23, 42, 0.6)',
        border: plan.highlighted
          ? '2px solid rgba(6, 182, 212, 0.5)'
          : '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '40px',
        position: 'relative',
        transform: plan.highlighted ? 'scale(1.05)' : 'scale(1)',
        boxShadow: plan.highlighted ? '0 20px 60px rgba(6, 182, 212, 0.2)' : 'none',
        opacity: isInView ? 1 : 0,
        transition: `all 0.6s ease-out ${index * 0.1}s`
      }}
    >
      {plan.badge && (
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
          padding: '6px 20px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          {plan.badge}
        </div>
      )}
      <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>{plan.name}</h3>
      <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '24px' }}>{plan.description}</p>
      <div style={{ marginBottom: '32px' }}>
        <span style={{ fontSize: '14px', color: '#64748b' }}>Starting at</span>
        <div style={{ fontSize: '36px', fontWeight: '700', marginTop: '4px' }} className="gradient-text">Contact Sales</div>
      </div>
      <div style={{ marginBottom: '32px' }}>
        {plan.features.map((feature, j) => (
          <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <Icon name="check" size={18} color="#10b981" />
            <span style={{ fontSize: '14px', color: '#cbd5e1' }}>{feature}</span>
          </div>
        ))}
      </div>
      <button style={{
        width: '100%',
        padding: '16px',
        borderRadius: '12px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s',
        background: plan.highlighted ? 'linear-gradient(135deg, #06b6d4, #8b5cf6)' : 'transparent',
        color: 'white',
        border: plan.highlighted ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        Get Started
      </button>
    </div>
  );
};

// ============================================
// INTEGRATION CARD COMPONENT
// ============================================
const IntegrationCard = ({ integration, index }) => {
  const [ref, isInView] = useInView();
  const [imgError, setImgError] = useState(false);

  return (
    <div
      ref={ref}
      className="hover-lift"
      style={{
        background: 'rgba(15, 23, 42, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        padding: '24px',
        textAlign: 'center',
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.4s ease-out ${index * 0.05}s`,
        cursor: 'pointer'
      }}
    >
      <div style={{
        width: '64px',
        height: '64px',
        background: imgError ? `${integration.color}20` : 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
        padding: '12px',
        overflow: 'hidden'
      }}>
        {!imgError && integration.logo ? (
          <img
            src={integration.logo}
            alt={integration.name}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span style={{
            fontSize: '24px',
            fontWeight: '700',
            color: integration.color
          }}>
            {integration.name.charAt(0)}
          </span>
        )}
      </div>
      <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>{integration.name}</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>{integration.type}</div>
    </div>
  );
};

// ============================================
// FLOATING PARTICLES COMPONENT
// ============================================
const FloatingParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 20,
    duration: 15 + Math.random() * 10,
    size: 2 + Math.random() * 4
  }));

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            bottom: '-20px',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: 'rgba(6, 182, 212, 0.4)',
            animation: `particle ${p.duration}s linear ${p.delay}s infinite`
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// OMNICHANNEL DEMO COMPONENT - Platform Selector
// ============================================
const OmnichannelDemo = () => {
  const [activePlatform, setActivePlatform] = useState('chat');
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentScenario, setCurrentScenario] = useState(0);

  // Platform definitions
  const platforms = [
    { id: 'chat', label: 'Web Chat', icon: 'support', color: '#06b6d4' },
    { id: 'voice', label: 'Voice', icon: 'phone', color: '#8b5cf6' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'chat', color: '#25D366' },
    { id: 'mobile', label: 'Mobile', icon: 'smartphone', color: '#f59e0b' }
  ];

  // Agent definitions
  const agents = {
    booking: { name: 'Booking Agent', color: '#06b6d4', icon: 'booking' },
    disruption: { name: 'Disruption Agent', color: '#f59e0b', icon: 'disruption' },
    loyalty: { name: 'Loyalty Agent', color: '#8b5cf6', icon: 'loyalty' },
    operations: { name: 'Operations Agent', color: '#10b981', icon: 'operations' },
    support: { name: 'Support Agent', color: '#3b82f6', icon: 'support' }
  };

  // Platform-specific scenarios
  const platformScenarios = {
    chat: [
      {
        messages: [
          { type: 'user', text: 'I need to book a flight from NYC to London tomorrow' },
          { type: 'bot', agent: 'booking', text: 'Found AX103 departing 8:00 AM. Booking confirmed! ✓' },
          { type: 'system', text: '⚡ Disruption detected — handing off...' },
          { type: 'bot', agent: 'disruption', text: 'Alert: AX103 delayed 3hrs. Rebooked on AX101 at 7:00 AM.' },
          { type: 'bot', agent: 'loyalty', text: 'Added 2,500 bonus miles for the inconvenience.' }
        ]
      }
    ],
    voice: [
      {
        messages: [
          { type: 'voice-user', text: '"I need to change my flight to tomorrow"', duration: '3s' },
          { type: 'voice-system', text: 'Transcribing speech...' },
          { type: 'bot', agent: 'booking', text: 'I found 3 available flights tomorrow. The earliest is AX205 at 6:15 AM.' },
          { type: 'voice-bot', text: 'Speaking response...', duration: '4s' },
          { type: 'bot', agent: 'booking', text: 'Flight changed successfully. Confirmation sent to your phone.' }
        ]
      }
    ],
    whatsapp: [
      {
        messages: [
          { type: 'wa-user', text: 'Hi, where is my bag? Flight AX442', time: '2:34 PM' },
          { type: 'wa-bot', agent: 'support', text: 'Let me track that for you...', time: '2:34 PM' },
          { type: 'wa-bot', agent: 'operations', text: '📍 Your bag is at JFK Terminal 4. Arriving on AX208 at 5:30 PM.', time: '2:35 PM' },
          { type: 'wa-bot', agent: 'operations', text: '🚗 Delivery scheduled to your hotel by 7 PM.', time: '2:35 PM' }
        ]
      }
    ],
    mobile: [
      {
        messages: [
          { type: 'notification', title: 'Flight Update', text: 'AX103 gate changed to B22', time: '1 min ago' },
          { type: 'notification', title: 'Boarding Soon', text: 'Boarding begins in 25 minutes', time: '5 min ago' },
          { type: 'card', title: 'Your Flight', flight: 'AX103', route: 'JFK → LHR', time: '8:00 AM', gate: 'B22', status: 'On Time' },
          { type: 'action', buttons: ['Mobile Pass', 'Lounge Access', 'Upgrade'] }
        ]
      }
    ]
  };

  const currentMessages = platformScenarios[activePlatform][currentScenario]?.messages || [];

  useEffect(() => {
    setMessageIndex(0);
    setCurrentScenario(0);
  }, [activePlatform]);

  useEffect(() => {
    const timer = setInterval(() => {
      setMessageIndex(prev => {
        if (prev >= currentMessages.length - 1) {
          return prev;
        }
        return prev + 1;
      });
    }, 2000);
    return () => clearInterval(timer);
  }, [activePlatform, currentMessages.length]);

  const getAgentIcon = (iconName) => Icons[iconName] || Icons.brain;

  // Render different UI based on platform
  const renderContent = () => {
    switch (activePlatform) {
      case 'voice':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '240px' }}>
            {currentMessages.slice(0, messageIndex + 1).map((msg, i) => (
              <div key={i} style={{ animation: 'fadeInUp 0.4s ease-out' }}>
                {msg.type === 'voice-user' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '4px' }}>CALLER</div>
                      <div style={{
                        background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                        padding: '12px 16px',
                        borderRadius: '16px 16px 4px 16px',
                        fontSize: '13px',
                        fontStyle: 'italic'
                      }}>{msg.text}</div>
                    </div>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      background: 'rgba(139, 92, 246, 0.2)', border: '2px solid #8b5cf6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Icon name="phone" size={18} color="#8b5cf6" />
                    </div>
                  </div>
                )}
                {msg.type === 'voice-system' && (
                  <div style={{
                    textAlign: 'center', fontSize: '11px', color: '#06b6d4',
                    padding: '8px', background: 'rgba(6, 182, 212, 0.1)',
                    borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[1,2,3,4,5].map(n => (
                        <div key={n} style={{
                          width: '3px', height: `${8 + Math.random() * 12}px`,
                          background: '#06b6d4', borderRadius: '2px',
                          animation: `soundwave 0.5s ease-in-out ${n * 0.1}s infinite alternate`
                        }} />
                      ))}
                    </div>
                    {msg.text}
                  </div>
                )}
                {msg.type === 'voice-bot' && (
                  <div style={{
                    textAlign: 'center', fontSize: '11px', color: '#10b981',
                    padding: '8px', background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[1,2,3,4,5].map(n => (
                        <div key={n} style={{
                          width: '3px', height: `${8 + Math.random() * 12}px`,
                          background: '#10b981', borderRadius: '2px',
                          animation: `soundwave 0.5s ease-in-out ${n * 0.1}s infinite alternate`
                        }} />
                      ))}
                    </div>
                    {msg.text}
                  </div>
                )}
                {msg.type === 'bot' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '10px',
                      background: `linear-gradient(135deg, ${agents[msg.agent].color}20, ${agents[msg.agent].color}10)`,
                      border: `1.5px solid ${agents[msg.agent].color}60`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <span style={{ display: 'inline-flex', width: 16, height: 16, color: agents[msg.agent].color }}>
                        {getAgentIcon(agents[msg.agent].icon)}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', color: agents[msg.agent].color, fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>
                        {agents[msg.agent].name}
                      </div>
                      <div style={{
                        background: 'rgba(30, 41, 59, 0.8)', padding: '10px 14px',
                        borderRadius: '6px 16px 16px 16px', fontSize: '12px', lineHeight: '1.5',
                        borderLeft: `3px solid ${agents[msg.agent].color}`
                      }}>{msg.text}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'whatsapp':
        return (
          <div style={{
            background: 'linear-gradient(180deg, #0a1014 0%, #111b21 100%)',
            borderRadius: '12px', padding: '12px', minHeight: '240px',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.02\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {currentMessages.slice(0, messageIndex + 1).map((msg, i) => (
                <div key={i} style={{ animation: 'fadeInUp 0.3s ease-out' }}>
                  {msg.type === 'wa-user' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{
                        background: '#005c4b', padding: '8px 12px', borderRadius: '8px 8px 0 8px',
                        maxWidth: '80%', fontSize: '13px', position: 'relative'
                      }}>
                        {msg.text}
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', textAlign: 'right', marginTop: '4px' }}>
                          {msg.time} ✓✓
                        </div>
                      </div>
                    </div>
                  )}
                  {msg.type === 'wa-bot' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{
                        background: '#202c33', padding: '8px 12px', borderRadius: '8px 8px 8px 0',
                        maxWidth: '80%', fontSize: '13px'
                      }}>
                        <div style={{ fontSize: '10px', color: agents[msg.agent].color, fontWeight: '600', marginBottom: '4px' }}>
                          {agents[msg.agent].name}
                        </div>
                        {msg.text}
                        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', textAlign: 'right', marginTop: '4px' }}>
                          {msg.time}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'mobile':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '240px' }}>
            {currentMessages.slice(0, messageIndex + 1).map((msg, i) => (
              <div key={i} style={{ animation: 'fadeInUp 0.4s ease-out' }}>
                {msg.type === 'notification' && (
                  <div style={{
                    background: 'rgba(30, 41, 59, 0.9)', borderRadius: '12px', padding: '12px',
                    border: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '12px', alignItems: 'center'
                  }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <AeronyxLogo size={20} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: '600' }}>{msg.title}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{msg.text}</div>
                    </div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>{msg.time}</div>
                  </div>
                )}
                {msg.type === 'card' && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(139, 92, 246, 0.15))',
                    borderRadius: '16px', padding: '16px', border: '1px solid rgba(6, 182, 212, 0.3)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{msg.title}</div>
                      <div style={{
                        fontSize: '10px', padding: '4px 8px', borderRadius: '12px',
                        background: msg.status === 'On Time' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                        color: msg.status === 'On Time' ? '#10b981' : '#f59e0b'
                      }}>{msg.status}</div>
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>{msg.flight}</div>
                    <div style={{ fontSize: '14px', color: '#e2e8f0', marginBottom: '12px' }}>{msg.route}</div>
                    <div style={{ display: 'flex', gap: '24px' }}>
                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>DEPARTURE</div>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>{msg.time}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '10px', color: '#64748b' }}>GATE</div>
                        <div style={{ fontSize: '14px', fontWeight: '600' }}>{msg.gate}</div>
                      </div>
                    </div>
                  </div>
                )}
                {msg.type === 'action' && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {msg.buttons.map((btn, j) => (
                      <div key={j} style={{
                        flex: 1, padding: '10px', borderRadius: '10px', textAlign: 'center',
                        background: 'rgba(51, 65, 85, 0.5)', border: '1px solid rgba(255,255,255,0.1)',
                        fontSize: '11px', fontWeight: '500', cursor: 'pointer'
                      }}>{btn}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      default: // chat
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '240px' }}>
            {currentMessages.slice(0, messageIndex + 1).map((msg, i) => (
              <div key={i} style={{ animation: 'fadeInUp 0.4s ease-out' }}>
                {msg.type === 'system' && (
                  <div style={{
                    textAlign: 'center', fontSize: '11px', color: '#f59e0b', padding: '8px 16px',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
                    borderRadius: '20px', border: '1px dashed rgba(245, 158, 11, 0.25)', fontWeight: '500'
                  }}>{msg.text}</div>
                )}
                {msg.type === 'user' && (
                  <div style={{
                    marginLeft: 'auto', background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                    padding: '12px 16px', borderRadius: '18px 18px 6px 18px', maxWidth: '85%',
                    fontSize: '13px', lineHeight: '1.5', boxShadow: '0 4px 16px rgba(6, 182, 212, 0.25)'
                  }}>{msg.text}</div>
                )}
                {msg.type === 'bot' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '10px',
                      background: `linear-gradient(135deg, ${agents[msg.agent].color}20, ${agents[msg.agent].color}10)`,
                      border: `1.5px solid ${agents[msg.agent].color}60`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      boxShadow: `0 4px 12px ${agents[msg.agent].color}20`
                    }}>
                      <span style={{ display: 'inline-flex', width: 16, height: 16, color: agents[msg.agent].color }}>
                        {getAgentIcon(agents[msg.agent].icon)}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '10px', color: agents[msg.agent].color, fontWeight: '600',
                        marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.8px'
                      }}>{agents[msg.agent].name}</div>
                      <div style={{
                        background: 'rgba(30, 41, 59, 0.8)', padding: '12px 14px',
                        borderRadius: '6px 16px 16px 16px', fontSize: '12px', lineHeight: '1.6',
                        borderLeft: `3px solid ${agents[msg.agent].color}`, color: 'rgba(255,255,255,0.9)'
                      }}>{msg.text}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {messageIndex < currentMessages.length - 1 && (
              <div style={{ display: 'flex', gap: '5px', padding: '10px', marginLeft: '42px' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#06b6d4', animation: 'typing 1s infinite' }} />
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#8b5cf6', animation: 'typing 1s infinite 0.2s' }} />
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ec4899', animation: 'typing 1s infinite 0.4s' }} />
              </div>
            )}
          </div>
        );
    }
  };

  const currentPlatform = platforms.find(p => p.id === activePlatform);

  return (
    <div className="omnichannel-demo" style={{
      background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.85))',
      backdropFilter: 'blur(24px)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '24px',
      padding: '24px',
      maxWidth: '420px',
      width: '100%',
      boxShadow: '0 32px 64px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 0 80px rgba(6, 182, 212, 0.08)',
      position: 'relative',
      zIndex: 10
    }}>
      {/* Platform selector tabs */}
      <div style={{
        display: 'flex', gap: '6px', marginBottom: '16px', padding: '4px',
        background: 'rgba(15, 23, 42, 0.6)', borderRadius: '14px'
      }}>
        {platforms.map(platform => (
          <button
            key={platform.id}
            onClick={() => setActivePlatform(platform.id)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
              padding: '10px 8px', borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: activePlatform === platform.id
                ? `linear-gradient(135deg, ${platform.color}30, ${platform.color}15)`
                : 'transparent',
              borderBottom: activePlatform === platform.id ? `2px solid ${platform.color}` : '2px solid transparent',
              transition: 'all 0.3s',
              color: activePlatform === platform.id ? platform.color : '#64748b'
            }}
          >
            <Icon name={platform.icon} size={18} color={activePlatform === platform.id ? platform.color : '#64748b'} />
            <span style={{ fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {platform.label}
            </span>
          </button>
        ))}
      </div>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            background: `linear-gradient(135deg, ${currentPlatform.color}20, ${currentPlatform.color}10)`,
            borderRadius: '10px', padding: '8px', border: `1px solid ${currentPlatform.color}30`
          }}>
            <AeronyxLogo size={28} />
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', letterSpacing: '-0.3px' }}>Aeronyx</div>
            <div style={{ fontSize: '10px', color: currentPlatform.color, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: '5px', height: '5px', background: currentPlatform.color, borderRadius: '50%', animation: 'pulse-glow 2s infinite' }} />
              {currentPlatform.label} Channel
            </div>
          </div>
        </div>
        <div style={{
          fontSize: '9px', padding: '4px 10px', borderRadius: '12px',
          background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: '600'
        }}>
          26 AGENTS
        </div>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

// ============================================
// ARCHITECTURE PAGE (unchanged from original)
// ============================================
const ArchitecturePage = ({ onBack }) => {
  return (
    <div style={{ minHeight: '100vh', background: '#030712' }}>
      <style>{globalStyles}</style>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 24px 0' }}>
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#94a3b8',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontFamily: '"Poppins", sans-serif'
          }}
        >
          <Icon name="arrowLeft" size={16} /> Back to Home
        </button>
      </div>
      <AeronyxArchitecture />
    </div>
  );
};

// ============================================
// MAIN WEBSITE COMPONENT
// ============================================
const AeronyxWebsite = () => {
  const [scrolled, setScrolled] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [hoveredAgent, setHoveredAgent] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Testimonial rotation
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % 4); // 4 testimonials
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  if (currentPage === 'architecture') {
    return <ArchitecturePage onBack={() => setCurrentPage('home')} />;
  }

  const testimonials = [
    { quote: "Aeronyx reduced our call center volume by 45% while improving CSAT scores across all channels. The disruption agent alone handled over 50,000 rebookings during our busiest month.", author: "Sarah Chen", role: "VP Customer Experience", airline: "Pacific Airways", logo: "PA" },
    { quote: "The disruption agent saved us $2.3M in the first year by automating rebookings and reducing compensation errors. Implementation was seamless with our Amadeus system.", author: "Marcus Thompson", role: "Chief Operating Officer", airline: "Atlantic Express", logo: "AE" },
    { quote: "Our Amadeus integration was live in under 8 weeks. The team's airline expertise made all the difference. We now handle 3x the volume with the same staff.", author: "Elena Rodriguez", role: "Chief Technology Officer", airline: "Meridian Airlines", logo: "MA" },
    { quote: "Customer satisfaction scores jumped 23 points after deploying the booking and loyalty agents. Our frequent flyers love the personalized experience.", author: "James Park", role: "Head of Digital", airline: "Skyline Aviation", logo: "SA" }
  ];

  const howItWorks = [
    { icon: 'plug', title: 'Connect Your Systems', description: 'Integrate with your existing PSS, DCS, CRM, and loyalty platforms via MCP protocol. Pre-built connectors for Amadeus, Sabre, and more.' },
    { icon: 'settings', title: 'Configure AI Agents', description: 'Customize agent behaviors, knowledge bases, brand voice, and escalation rules. Define business logic without code.' },
    { icon: 'rocket', title: 'Deploy Across Channels', description: 'Go live on voice IVR, web chat, mobile app, WhatsApp, and agent desktop simultaneously with unified context.' },
    { icon: 'chart', title: 'Monitor & Optimize', description: 'Track performance metrics, analyze conversations, identify improvement opportunities, and continuously enhance.' }
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      description: 'Perfect for regional airlines starting their AI journey',
      features: ['Up to 100K conversations/month', '3 AI agents included', 'Email support', 'Basic analytics dashboard', 'Standard integrations', '99.5% uptime SLA'],
      highlighted: false
    },
    {
      name: 'Professional',
      description: 'Most popular for growing airlines',
      features: ['Up to 1M conversations/month', 'All 6 AI agents', 'Priority 24/7 support', 'Advanced analytics & insights', 'Custom integrations', '99.9% uptime SLA', 'Dedicated success manager', 'Custom knowledge base'],
      highlighted: true,
      badge: 'Most Popular'
    },
    {
      name: 'Enterprise',
      description: 'For global carriers with complex needs',
      features: ['Unlimited conversations', 'All agents + custom agents', 'Premium 24/7 support', 'Enterprise analytics suite', 'All integrations included', '99.99% uptime SLA', 'Dedicated team', 'On-premise deployment option', 'Custom SLAs'],
      highlighted: false
    }
  ];

  const integrations = [
    { name: 'Amadeus', type: 'PSS', color: '#0066cc', logo: 'https://1000logos.net/wp-content/uploads/2022/05/Amadeus-Logo-tumb.png' },
    { name: 'Sabre', type: 'PSS', color: '#cc0000', logo: 'https://companieslogo.com/img/orig/SABR-97638dc1.png' },
    { name: 'Navitaire', type: 'PSS', color: '#005eb8', logo: 'https://cdnlogo.com/logos/n/35/navitaire.svg' },
    { name: 'Travelport', type: 'GDS', color: '#0033a0', logo: 'https://companieslogo.com/img/orig/TVPT-6ad71cd8.png' },
    { name: 'SITA', type: 'Operations', color: '#e31937', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/SITA_logo.svg/800px-SITA_logo.svg.png' },
    { name: 'Comarch', type: 'Loyalty', color: '#e4002b', logo: 'https://companieslogo.com/img/orig/CMR.WA-86c5b9a4.png' },
    { name: 'PROS', type: 'Revenue', color: '#00a3e0', logo: 'https://companieslogo.com/img/orig/PRO-e5e1c3fd.png' },
    { name: 'Salesforce', type: 'CRM', color: '#00a1e0', logo: 'https://1000logos.net/wp-content/uploads/2019/06/Salesforce-Logo.jpg' },
    { name: 'ElevenLabs', type: 'Voice', color: '#000000', logo: 'https://seeklogo.com/images/E/elevenlabs-logo-40F5E83610-seeklogo.com.png' },
    { name: 'Twilio', type: 'Communications', color: '#f22f46', logo: 'https://1000logos.net/wp-content/uploads/2024/08/Twilio-Logo-tumb.png' },
    { name: 'Zendesk', type: 'Support', color: '#03363d', logo: 'https://1000logos.net/wp-content/uploads/2020/05/Zendesk-Logo-tumb.jpg' },
    { name: 'Jeppesen', type: 'Crew', color: '#0077b5', logo: 'https://1000logos.net/wp-content/uploads/2023/05/Jeppesen-Logo-tumb.png' }
  ];

  const faqs = [
    { q: 'How long does implementation take?', a: 'Typical implementation takes 6-8 weeks for standard PSS integrations. We provide pre-built connectors for Amadeus, Sabre, and Navitaire that accelerate deployment. Complex enterprise deployments with custom requirements may take 12-16 weeks.' },
    { q: 'What languages are supported?', a: 'Aeronyx supports 40+ languages out of the box, including all major languages for aviation. Our AI agents can seamlessly switch languages mid-conversation and maintain context across language changes.' },
    { q: 'How does the AI handle edge cases?', a: 'Our agents are trained on millions of airline-specific interactions. For complex cases, they gracefully escalate to human agents while preserving full conversation context. You can customize escalation rules based on your business needs.' },
    { q: 'Is my passenger data secure?', a: 'Absolutely. We are SOC 2 Type II certified, GDPR compliant, and PCI-DSS certified for payment handling. All data is encrypted at rest and in transit. We offer data residency options for different regions.' },
    { q: 'Can I customize the AI responses?', a: 'Yes! You can fully customize the brand voice, response templates, knowledge base, and business rules. Our no-code configuration interface makes it easy to adjust behaviors without engineering resources.' },
    { q: 'What happens during system outages?', a: 'Aeronyx maintains 99.99% uptime with multi-region redundancy. In rare outage scenarios, we have automatic failover and graceful degradation that routes passengers to human support.' }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#030712', color: 'white', fontFamily: '"Poppins", sans-serif', overflowX: 'hidden' }}>
      <style>{globalStyles}</style>

      {/* ============================================ */}
      {/* NAVIGATION */}
      {/* ============================================ */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '16px 0',
        background: scrolled ? 'rgba(3, 7, 18, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AeronyxLogo size={46} />
            <span style={{ fontSize: '32px', fontWeight: '700', letterSpacing: '-1px' }}>
              Aero<span style={{ color: '#06b6d4' }}>nyx</span>
            </span>
          </div>
          {/* Desktop Navigation */}
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <a href="#agents" className="nav-link">Agents</a>
            <a href="#pricing" className="nav-link">Pricing</a>
            <a href="#integrations" className="nav-link">Integrations</a>
            <button className="cta-button">Request Demo</button>
          </div>
          {/* Mobile Menu Button - hidden when menu is open */}
          {!mobileMenuOpen && (
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>
              <Icon name="menu" size={28} color="white" />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-nav open">
          <button type="button" className="mobile-close-btn" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: 'none' }}>
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        <a href="#how-it-works" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
        <a href="#agents" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Agents</a>
        <a href="#pricing" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
        <a href="#integrations" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Integrations</a>
        <button className="cta-button" onClick={() => setMobileMenuOpen(false)} style={{ marginTop: '16px' }}>
          Request Demo
        </button>
        </div>
      )}

      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <section style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }} className="grid-bg">
        <FloatingParticles />

        {/* Gradient orbs */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)', borderRadius: '50%', filter: 'blur(60px)', animation: 'pulse-glow 8s infinite' }} />
        <div style={{ position: 'absolute', bottom: '10%', left: '10%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)', borderRadius: '50%', filter: 'blur(60px)', animation: 'pulse-glow 10s infinite 2s' }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(100px, 15vw, 140px) clamp(16px, 5vw, 40px) clamp(40px, 10vw, 80px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'clamp(32px, 6vw, 60px)', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          {/* Left content */}
          <div style={{ animation: 'slide-up 1s ease-out' }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              borderRadius: '50px',
              padding: '8px 20px',
              marginBottom: '28px'
            }}>
              <span style={{ width: '8px', height: '8px', background: '#06b6d4', borderRadius: '50%', animation: 'pulse-glow 2s infinite' }} />
              <span style={{ fontSize: '13px', color: '#06b6d4', fontWeight: '500' }}>Now with MCP Protocol Support</span>
            </div>

            <h1 style={{ fontSize: 'clamp(36px, 8vw, 64px)', fontWeight: '700', lineHeight: '1.1', letterSpacing: '-2px', marginBottom: '24px' }}>
              AI Agents for <br />
              <span className="gradient-text">Modern Airlines</span>
            </h1>

            <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', lineHeight: '1.7', color: '#94a3b8', marginBottom: '32px', maxWidth: '520px' }}>
              Multiple AI agents. One ecosystem. From booking to boarding, every interaction intelligently orchestrated.
            </p>

            {/* Trust badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '40px' }}>
              {[
                { label: 'SOC 2', icon: 'shield' },
                { label: 'GDPR', icon: 'shield' },
                { label: 'PCI-DSS', icon: 'shield' }
              ].map((badge, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '13px' }}>
                  <Icon name={badge.icon} size={16} color="#06b6d4" />
                  {badge.label}
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <button className="cta-button">
                Schedule Demo <Icon name="arrowRight" size={18} />
              </button>
              <button className="secondary-button" onClick={() => setCurrentPage('architecture')}>
                View Architecture
              </button>
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(20px, 4vw, 40px)', marginTop: '48px' }}>
              {[
                { value: '2.4M+', label: 'Bookings' },
                { value: '94%', label: 'Satisfaction' },
                { value: '<2s', label: 'Response' }
              ].map((stat, i) => (
                <div key={i}>
                  <div style={{ fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: '700', fontFamily: '"Space Mono", monospace' }} className="gradient-text">{stat.value}</div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right content - Demo with Agent Grid */}
          <div className="hero-demo-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0', animation: 'slide-up 1s ease-out 0.3s both', position: 'relative' }}>
            {/* Top row - 3 agents */}
            <div className="orbiting-agents" style={{ display: 'flex', gap: '12px', justifyContent: 'center', zIndex: 2 }}>
              {agentsData.slice(0, 3).map((agent, i) => (
                <div
                  key={agent.id}
                  style={{
                    width: '130px',
                    padding: '14px 10px',
                    background: `linear-gradient(145deg, ${agent.color}12, ${agent.color}05)`,
                    border: `1.5px solid ${agent.color}35`,
                    borderRadius: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    animation: `float ${5 + i * 0.5}s ease-in-out infinite ${i * 0.15}s`,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 4px 20px ${agent.color}08`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05) translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 12px 30px ${agent.color}25`;
                    e.currentTarget.style.borderColor = agent.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 20px ${agent.color}08`;
                    e.currentTarget.style.borderColor = `${agent.color}35`;
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${agent.color}25, ${agent.color}10)`,
                    border: `1px solid ${agent.color}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon name={agent.icon} size={20} color={agent.color} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: agent.color, textAlign: 'center', lineHeight: '1.3' }}>
                    {agent.name.replace(' Agent', '')}
                  </span>
                </div>
              ))}
            </div>

            {/* Connection lines from top agents to center */}
            <svg width="414" height="28" className="connection-lines" style={{ overflow: 'visible', zIndex: 1 }}>
              <defs>
                <linearGradient id="topLine1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={agentsData[0].color} stopOpacity="0.6" />
                  <stop offset="100%" stopColor={agentsData[0].color} stopOpacity="0.15" />
                </linearGradient>
                <linearGradient id="topLine3" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={agentsData[2].color} stopOpacity="0.6" />
                  <stop offset="100%" stopColor={agentsData[2].color} stopOpacity="0.15" />
                </linearGradient>
              </defs>
              {/* Left: Booking agent to center */}
              <path d="M 65 0 Q 65 14, 207 28" stroke="url(#topLine1)" strokeWidth="2" fill="none" strokeDasharray="4 3">
                <animate attributeName="stroke-dashoffset" from="7" to="0" dur="1s" repeatCount="indefinite" />
              </path>
              {/* Center: Disruption agent straight down */}
              <path d="M 207 0 L 207 28" stroke={agentsData[1].color} strokeWidth="2" fill="none" strokeDasharray="4 3" strokeOpacity="0.5">
                <animate attributeName="stroke-dashoffset" from="7" to="0" dur="1s" repeatCount="indefinite" />
              </path>
              {/* Right: Loyalty agent to center */}
              <path d="M 349 0 Q 349 14, 207 28" stroke="url(#topLine3)" strokeWidth="2" fill="none" strokeDasharray="4 3">
                <animate attributeName="stroke-dashoffset" from="7" to="0" dur="1s" repeatCount="indefinite" />
              </path>
            </svg>

            {/* Center Omnichannel Demo */}
            <OmnichannelDemo />

            {/* Connection lines from center to bottom agents */}
            <svg width="414" height="28" className="connection-lines" style={{ overflow: 'visible', zIndex: 1 }}>
              <defs>
                <linearGradient id="botLine1" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={agentsData[3].color} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={agentsData[3].color} stopOpacity="0.6" />
                </linearGradient>
                <linearGradient id="botLine3" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={agentsData[5].color} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={agentsData[5].color} stopOpacity="0.6" />
                </linearGradient>
              </defs>
              {/* Center to Left: Operations agent */}
              <path d="M 207 0 Q 65 14, 65 28" stroke="url(#botLine1)" strokeWidth="2" fill="none" strokeDasharray="4 3">
                <animate attributeName="stroke-dashoffset" from="0" to="7" dur="1s" repeatCount="indefinite" />
              </path>
              {/* Center to Center: Revenue agent */}
              <path d="M 207 0 L 207 28" stroke={agentsData[4].color} strokeWidth="2" fill="none" strokeDasharray="4 3" strokeOpacity="0.5">
                <animate attributeName="stroke-dashoffset" from="0" to="7" dur="1s" repeatCount="indefinite" />
              </path>
              {/* Center to Right: Support agent */}
              <path d="M 207 0 Q 349 14, 349 28" stroke="url(#botLine3)" strokeWidth="2" fill="none" strokeDasharray="4 3">
                <animate attributeName="stroke-dashoffset" from="0" to="7" dur="1s" repeatCount="indefinite" />
              </path>
            </svg>

            {/* Bottom row - 3 agents */}
            <div className="orbiting-agents" style={{ display: 'flex', gap: '12px', justifyContent: 'center', zIndex: 2 }}>
              {agentsData.slice(3, 6).map((agent, i) => (
                <div
                  key={agent.id}
                  style={{
                    width: '130px',
                    padding: '14px 10px',
                    background: `linear-gradient(145deg, ${agent.color}12, ${agent.color}05)`,
                    border: `1.5px solid ${agent.color}35`,
                    borderRadius: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    animation: `float ${5.5 + i * 0.5}s ease-in-out infinite ${i * 0.2}s`,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    backdropFilter: 'blur(10px)',
                    boxShadow: `0 4px 20px ${agent.color}08`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05) translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 12px 30px ${agent.color}25`;
                    e.currentTarget.style.borderColor = agent.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1) translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 20px ${agent.color}08`;
                    e.currentTarget.style.borderColor = `${agent.color}35`;
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${agent.color}25, ${agent.color}10)`,
                    border: `1px solid ${agent.color}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon name={agent.icon} size={20} color={agent.color} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: agent.color, textAlign: 'center', lineHeight: '1.3' }}>
                    {agent.name.replace(' Agent', '')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* STATS SECTION */}
      {/* ============================================ */}
      <section style={{ padding: 'clamp(60px, 10vw, 100px) clamp(16px, 5vw, 40px)', background: 'linear-gradient(180deg, transparent, rgba(6, 182, 212, 0.02))' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
            {[
              { value: 2400000, suffix: '+', label: 'Bookings Processed', icon: 'chart', color: '#06b6d4' },
              { value: 94, suffix: '%', label: 'Customer Satisfaction', icon: 'loyalty', color: '#8b5cf6' },
              { value: 67, suffix: '%', label: 'Cost Reduction', icon: 'revenue', color: '#10b981' },
              { value: 2, suffix: 's', prefix: '<', label: 'Avg Response Time', icon: 'disruption', color: '#f59e0b' }
            ].map((stat, i) => (
              <StatCard key={i} stat={stat} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW IT WORKS SECTION */}
      {/* ============================================ */}
      <HowItWorksSection howItWorks={howItWorks} />

      {/* ============================================ */}
      {/* AGENTS SECTION */}
      {/* ============================================ */}
      <section id="agents" style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)', background: 'linear-gradient(180deg, rgba(6, 182, 212, 0.02), transparent)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 8vw, 80px)' }}>
            <h2 style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '700', marginBottom: '20px' }}>
              Core <span className="gradient-text">AI Agents</span>
            </h2>
            <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
              Part of our 26-agent ecosystem. Each agent is purpose-built for a specific domain, working together seamlessly.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }} className="agents-grid">
            {agentsData.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                isHovered={hoveredAgent === agent.id}
                onHover={() => setHoveredAgent(agent.id)}
                onLeave={() => setHoveredAgent(null)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRICING SECTION */}
      {/* ============================================ */}
      <section id="pricing" style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 8vw, 80px)' }}>
            <h2 style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '700', marginBottom: '20px' }}>
              Simple, Transparent <span className="gradient-text">Pricing</span>
            </h2>
            <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
              Choose the plan that fits your airline's needs. All plans include our core platform and dedicated onboarding.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', alignItems: 'stretch' }}>
            {pricingPlans.map((plan, i) => (
              <PricingCard key={i} plan={plan} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* INTEGRATIONS SECTION */}
      {/* ============================================ */}
      <section id="integrations" style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)', background: 'linear-gradient(180deg, transparent, rgba(6, 182, 212, 0.02))' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 8vw, 60px)' }}>
            <h2 style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '700', marginBottom: '20px' }}>
              Seamless <span className="gradient-text">Integrations</span>
            </h2>
            <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
              Pre-built connectors for all major airline systems. Deploy in weeks, not months.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
            {integrations.map((integration, i) => (
              <IntegrationCard key={i} integration={integration} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* TESTIMONIALS SECTION */}
      {/* ============================================ */}
      <section style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 8vw, 60px)' }}>
            <h2 style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '700', marginBottom: '20px' }}>
              Trusted by <span className="gradient-text">Industry Leaders</span>
            </h2>
          </div>

          <div style={{ position: 'relative' }}>
            {testimonials.map((t, i) => (
              <div
                key={i}
                style={{
                  display: activeTestimonial === i ? 'block' : 'none',
                  animation: 'fadeInUp 0.5s ease-out'
                }}
              >
                <div style={{
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.5))',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  padding: 'clamp(24px, 5vw, 48px)',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{ position: 'absolute', top: '24px', left: '24px', opacity: 0.2 }}>
                    <Icon name="quote" size={60} style={{ width: 'clamp(40px, 8vw, 60px)', height: 'clamp(40px, 8vw, 60px)' }} />
                  </div>

                  <p style={{ fontSize: 'clamp(16px, 3vw, 22px)', lineHeight: '1.8', color: '#e2e8f0', marginBottom: '32px', position: 'relative', zIndex: 1 }}>
                    "{t.quote}"
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '700'
                    }}>
                      {t.logo}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{t.author}</div>
                      <div style={{ fontSize: '14px', color: '#64748b' }}>{t.role}</div>
                      <div style={{ fontSize: '14px', color: '#06b6d4', marginTop: '2px' }}>{t.airline}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  style={{
                    width: activeTestimonial === i ? '32px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: activeTestimonial === i ? 'linear-gradient(135deg, #06b6d4, #8b5cf6)' : 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FAQ SECTION */}
      {/* ============================================ */}
      <section style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)', background: 'linear-gradient(180deg, rgba(6, 182, 212, 0.02), transparent)' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 8vw, 60px)' }}>
            <h2 style={{ fontSize: 'clamp(32px, 6vw, 48px)', fontWeight: '700', marginBottom: '20px' }}>
              Frequently Asked <span className="gradient-text">Questions</span>
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {faqs.map((faq, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'all 0.3s'
                }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  {faq.q}
                  <Icon
                    name="chevronDown"
                    size={20}
                    color="#64748b"
                    style={{
                      transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.3s'
                    }}
                  />
                </button>
                <div style={{
                  maxHeight: openFaq === i ? '200px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease-out'
                }}>
                  <p style={{ padding: '0 24px 20px', color: '#94a3b8', fontSize: '15px', lineHeight: '1.7' }}>
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* CTA SECTION */}
      {/* ============================================ */}
      <section style={{ padding: 'clamp(60px, 12vw, 120px) clamp(16px, 5vw, 40px)', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(139, 92, 246, 0.15))',
          transform: 'skewY(-3deg)',
          transformOrigin: 'top left'
        }} />
        <FloatingParticles />

        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontSize: 'clamp(28px, 6vw, 52px)', fontWeight: '700', marginBottom: '24px' }}>
            Ready to Deploy Your <br />
            <span className="gradient-text">AI Agent Ecosystem</span>?
          </h2>
          <p style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#94a3b8', marginBottom: '48px', maxWidth: '600px', margin: '0 auto 48px' }}>
            Join leading airlines already using Aeronyx's multi-agent platform to transform operations.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            <button className="cta-button" style={{ padding: 'clamp(14px, 2vw, 18px) clamp(24px, 4vw, 48px)', fontSize: 'clamp(14px, 2vw, 16px)' }}>
              Schedule Demo <Icon name="arrowRight" size={20} />
            </button>
            <button className="secondary-button" style={{ padding: 'clamp(14px, 2vw, 18px) clamp(24px, 4vw, 48px)', fontSize: 'clamp(14px, 2vw, 16px)' }} onClick={() => setCurrentPage('architecture')}>
              View Architecture
            </button>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer style={{ padding: 'clamp(40px, 8vw, 80px) clamp(16px, 5vw, 40px) clamp(24px, 4vw, 40px)', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'clamp(24px, 4vw, 48px)', marginBottom: 'clamp(40px, 6vw, 60px)' }}>
            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <AeronyxLogo size={38} />
                <span style={{ fontSize: '20px', fontWeight: '700' }}>Aero<span style={{ color: '#06b6d4' }}>nyx</span></span>
              </div>
              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.7', marginBottom: '20px' }}>
                Multi-agent AI platform for airlines. 26 specialized agents orchestrating bookings, operations, and passenger experiences.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {['twitter', 'linkedin', 'github'].map((social, i) => (
                  <button key={i} type="button" style={{ width: '36px', height: '36px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', transition: 'all 0.3s', border: 'none', cursor: 'pointer' }}>
                    <Icon name={social} size={16} />
                  </button>
                ))}
              </div>
            </div>

            {/* Links */}
            {[
              { title: 'Product', links: ['Features', 'Agents', 'Integrations', 'Pricing', 'Security'] },
              { title: 'Company', links: ['About', 'Careers', 'Press', 'Partners', 'Contact'] },
              { title: 'Resources', links: ['Documentation', 'API Reference', 'Case Studies', 'Blog', 'Webinars'] },
              { title: 'Legal', links: ['Privacy', 'Terms', 'Cookie Policy', 'GDPR', 'SLA'] }
            ].map((section, i) => (
              <div key={i}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '20px', color: 'white' }}>{section.title}</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {section.links.map((link, j) => (
                    <li key={j} style={{ marginBottom: '12px' }}>
                      <button type="button" style={{ fontSize: '14px', color: '#64748b', textDecoration: 'none', transition: 'color 0.3s', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{link}</button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px', paddingTop: '32px', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <p style={{ fontSize: '14px', color: '#475569' }}>© 2025 Aeronyx. All rights reserved.</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '13px' }}>
              <Icon name="globe" size={16} />
              English (US)
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(6, 182, 212, 0.4)',
            zIndex: 999,
            transition: 'transform 0.3s, box-shadow 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 30px rgba(6, 182, 212, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(6, 182, 212, 0.4)';
          }}
          aria-label="Scroll to top"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default AeronyxWebsite;
